import { useCallback, useMemo } from "react";
import {
  Alert,
  AlertAction,
  AlertDescription,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Icon,
  Separator,
  TabsList,
  TabsTab,
  Text,
} from "@salestrak/ui";
import { EntityBreadcrumbLink } from "#app/components/entity";
import type { IssueRow } from "#app/lib/data";
import { useIssueCardActions } from "#app/hooks/kanban/use-issue-card-actions";
import { useAppData } from "#app/lib/data";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useIssueViewCapabilities } from "./issue-view-capabilities";
import { useIssueViewOptional } from "./use-issue-view";
import type { Omittable } from "@salestrak/ui";

type IssueBreadcrumbProps = Omittable<{
  issue?: IssueRow;
}>;

export function IssueBreadcrumb({ issue }: IssueBreadcrumbProps) {
  const { workflows } = useAppData();
  const createCtx = useCreateIssueMode();
  const issueView = useIssueViewOptional();
  const { push } = useAppNavigate();
  const isCreateMode = !!createCtx;

  const workflowId = isCreateMode
    ? createCtx.draft.workflowId
    : issue?.workflowId;

  const workflow = useMemo(
    () => workflows.find((wf) => wf.workflowId === workflowId),
    [workflows, workflowId],
  );

  const handleWorkflowClick = useCallback(() => {
    if (!workflowId) return;
    issueView?.closeIssue();
    push(`/workflow/${workflowId}`);
  }, [workflowId, issueView, push]);

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <EntityBreadcrumbLink
            entity={{
              kind: "workflow",
              id: workflowId ?? "",
              workflow: workflow ?? { name: "Workflow" },
            }}
            onClick={isCreateMode ? undefined : handleWorkflowClick}
          />
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          {/* Raw string — BreadcrumbPage carries the page-title type and
              truncates; a nested default-size Text shrinks it to sm on
              desktop. */}
          <BreadcrumbPage>
            {isCreateMode ? "New Project" : (issue?.name ?? "")}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

type IssueViewHeaderProps = Omittable<{
  issue?: IssueRow;
  /** Aggregated photo count shown on the Media tab label. */
  mediaCount?: number;
  /** Whether the issue has an Attachments tab — the body owns that decision. */
  showMediaTab?: boolean;
}>;

export function IssueViewHeader({ issue, mediaCount, showMediaTab }: IssueViewHeaderProps) {
  const createCtx = useCreateIssueMode();
  const { unarchiveIssue } = useIssueCardActions();
  const isCreateMode = !!createCtx;

  const handleUnarchive = useCallback(async () => {
    if (!issue) return;
    await unarchiveIssue(issue);
  }, [issue, unarchiveIssue]);

  return (
    <Box>
      {!isCreateMode && (
        <Box paddingX="md" paddingTop="xs">
          <TabsList variant="underline">
            <TabsTab value="summary">
              <Icon name="layout-dashboard" size="xs" />
              Summary
            </TabsTab>
            <TabsTab value="activity">
              <Icon name="activity" size="xs" />
              Activity
            </TabsTab>
            {showMediaTab ? (
              <TabsTab value="media">
                <Icon name="image" size="xs" />
                Attachments
                <Badge size="sm">{mediaCount ?? 0}</Badge>
              </TabsTab>
            ) : null}
          </TabsList>
        </Box>
      )}

      <Separator />

      {!isCreateMode && issue?.archived && (
        <Alert variant="warning">
          <AlertDescription>This project has been archived.</AlertDescription>
          <AlertAction>
            <Button variant="link" size="sm" onClick={handleUnarchive}>
              Unarchive
            </Button>
          </AlertAction>
        </Alert>
      )}

      <ReadOnlyAccessBanner />
    </Box>
  );
}

// Per-issue access banner — shows a muted info bar when the
// viewer's effective access is view or comment, so it's obvious why
// edit controls are inert. `full_edit` / `edit` viewers don't see
// the banner. Create mode skips it (the issue doesn't exist yet, no
// per-object capability applies).
function ReadOnlyAccessBanner() {
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const { level, canEdit } = useIssueViewCapabilities();
  if (isCreateMode || canEdit || level == null) return null;
  const message =
    level === "comment"
      ? "You have comment-only access on this project. You can post notes but can't edit fields."
      : "You have view-only access on this project. You can read it but can't edit fields or post comments.";
  return (
    <Alert variant="info">
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}
